import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTicketAlt, FaFilter, FaSearch, FaPlus, FaUser, FaClock, FaExclamation } from 'react-icons/fa';
import useSupport from '../hooks/useSupport';
import '../styles/Support.css';

const Support = () => {
  const navigate = useNavigate();
  const {
    tickets,
    stats,
    categories,
    agents,
    loading,
    error,
    pagination,
    fetchTickets,
    fetchStats
  } = useSupport();

  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: '',
    search: '',
    assigned_to: ''
  });

  const [showFilters, setShowFilters] = useState(false);

  // Appliquer les filtres
  const applyFilters = () => {
    fetchTickets(1, filters);
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    const emptyFilters = {
      status: '',
      priority: '',
      category: '',
      search: '',
      assigned_to: ''
    };
    setFilters(emptyFilters);
    fetchTickets(1, emptyFilters);
  };

  // Changer de page
  const changePage = (newPage) => {
    fetchTickets(newPage, filters);
  };

  // Formater la date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR');
  };

  // Obtenir la couleur du statut
  const getStatusColor = (status) => {
    const colors = {
      'open': '#e74c3c',
      'in_progress': '#f39c12',
      'resolved': '#27ae60',
      'closed': '#95a5a6'
    };
    return colors[status] || '#95a5a6';
  };

  // Obtenir la couleur de la priorité
  const getPriorityColor = (priority) => {
    const colors = {
      'urgent': '#e74c3c',
      'high': '#f39c12',
      'normal': '#3498db',
      'low': '#95a5a6'
    };
    return colors[priority] || '#95a5a6';
  };

  // Obtenir le nom complet de l'utilisateur
  const getUserName = (ticket) => {
    if (ticket.business_name) {
      return ticket.business_name;
    }
    if (ticket.client_first_name && ticket.client_last_name) {
      return `${ticket.client_first_name} ${ticket.client_last_name}`;
    }
    if (ticket.prestataire_first_name && ticket.prestataire_last_name) {
      return `${ticket.prestataire_first_name} ${ticket.prestataire_last_name}`;
    }
    return ticket.user_email;
  };

  if (error) {
    return (
      <div className="support-page">
        <div className="error-container">
          <p>Erreur: {error}</p>
          <button onClick={() => window.location.reload()}>Réessayer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="support-page">
      {/* Header */}
      <div className="support-header">
        <div className="header-left">
          <FaTicketAlt className="page-icon" />
          <div>
            <h1>Support Client</h1>
            <p>Gestion des tickets de support et demandes d'assistance</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="filter-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter />
            Filtres
          </button>
        </div>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-icon total">
              <FaTicketAlt />
            </div>
            <div className="stat-content">
              <h3>{stats.total_tickets || 0}</h3>
              <p>Total tickets</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon open">
              <FaExclamation />
            </div>
            <div className="stat-content">
              <h3>{stats.open_tickets || 0}</h3>
              <p>Ouverts</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon progress">
              <FaClock />
            </div>
            <div className="stat-content">
              <h3>{stats.in_progress_tickets || 0}</h3>
              <p>En cours</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon resolved">
              <FaUser />
            </div>
            <div className="stat-content">
              <h3>{stats.resolved_tickets || 0}</h3>
              <p>Résolus</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon unassigned">
              <FaUser />
            </div>
            <div className="stat-content">
              <h3>{stats.unassigned_tickets || 0}</h3>
              <p>Non assignés</p>
            </div>
          </div>

          {stats.avg_resolution_time_hours && (
            <div className="stat-card">
              <div className="stat-icon time">
                <FaClock />
              </div>
              <div className="stat-content">
                <h3>{Math.round(stats.avg_resolution_time_hours)}h</h3>
                <p>Temps moyen</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filtres */}
      {showFilters && (
        <div className="filters-section">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Recherche</label>
              <div className="search-input">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Rechercher dans les tickets..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
                />
              </div>
            </div>

            <div className="filter-group">
              <label>Statut</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">Tous les statuts</option>
                <option value="open">Ouvert</option>
                <option value="in_progress">En cours</option>
                <option value="resolved">Résolu</option>
                <option value="closed">Fermé</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Priorité</label>
              <select
                value={filters.priority}
                onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
              >
                <option value="">Toutes les priorités</option>
                <option value="urgent">Urgent</option>
                <option value="high">Élevée</option>
                <option value="normal">Normale</option>
                <option value="low">Faible</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Catégorie</label>
              <select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
              >
                <option value="">Toutes les catégories</option>
                {categories.map((category) => (
                  <option key={category.name} value={category.name}>
                    {category.name} ({category.count})
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Assigné à</label>
              <select
                value={filters.assigned_to}
                onChange={(e) => setFilters(prev => ({ ...prev, assigned_to: e.target.value }))}
              >
                <option value="">Tous les agents</option>
                <option value="unassigned">Non assigné</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.email} ({agent.active_tickets} actifs)
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filters-actions">
            <button className="btn-apply" onClick={applyFilters}>
              Appliquer les filtres
            </button>
            <button className="btn-reset" onClick={resetFilters}>
              Réinitialiser
            </button>
          </div>
        </div>
      )}

      {/* Liste des tickets */}
      <div className="tickets-section">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Chargement des tickets...</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="empty-state">
            <FaTicketAlt className="empty-icon" />
            <h3>Aucun ticket trouvé</h3>
            <p>Il n'y a actuellement aucun ticket de support correspondant à vos critères.</p>
          </div>
        ) : (
          <>
            <div className="tickets-list">
              {tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="ticket-card"
                  onClick={() => navigate(`/support/${ticket.id}`)}
                >
                  <div className="ticket-header">
                    <div className="ticket-title">
                      <h3>{ticket.subject}</h3>
                      <div className="ticket-meta">
                        <span className="ticket-id">#{ticket.id.substring(0, 8)}</span>
                        <span className="ticket-user">
                          <FaUser /> {getUserName(ticket)}
                        </span>
                        <span className="ticket-email">{ticket.user_email}</span>
                      </div>
                    </div>
                    <div className="ticket-badges">
                      <span
                        className="badge priority"
                        style={{ backgroundColor: getPriorityColor(ticket.priority) }}
                      >
                        {ticket.priority}
                      </span>
                      <span
                        className="badge status"
                        style={{ backgroundColor: getStatusColor(ticket.status) }}
                      >
                        {ticket.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>

                  <div className="ticket-content">
                    <p className="ticket-description">
                      {ticket.description.length > 150
                        ? `${ticket.description.substring(0, 150)}...`
                        : ticket.description
                      }
                    </p>
                  </div>

                  <div className="ticket-footer">
                    <div className="ticket-info">
                      <span className="category">
                        Catégorie: <strong>{ticket.category}</strong>
                      </span>
                      {ticket.message_count > 0 && (
                        <span className="messages">
                          {ticket.message_count} message(s)
                        </span>
                      )}
                    </div>
                    <div className="ticket-dates">
                      <span className="created">
                        Créé: {formatDate(ticket.created_at)}
                      </span>
                      {ticket.last_message_at && (
                        <span className="updated">
                          Dernière activité: {formatDate(ticket.last_message_at)}
                        </span>
                      )}
                    </div>
                  </div>

                  {ticket.assigned_email && (
                    <div className="ticket-assignment">
                      <FaUser />
                      <span>Assigné à: {ticket.assigned_email}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  disabled={pagination.page <= 1}
                  onClick={() => changePage(pagination.page - 1)}
                >
                  Précédent
                </button>

                <div className="pagination-info">
                  Page {pagination.page} sur {pagination.totalPages}
                  <br />
                  {tickets.length} tickets sur {pagination.total}
                </div>

                <button
                  className="pagination-btn"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => changePage(pagination.page + 1)}
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Support;